import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Eyebrow from '../components/common/Eyebrow';
import SectionHeader from '../components/common/SectionHeader';
import Reveal from '../components/common/Reveal';
import HeroSection from '../components/home/HeroSection';
import {
    AboutHeroStatsShimmer,
    AboutTimelineShimmer,
    AboutNumbersShimmer,
    AboutTeamShimmer,
} from '../components/shimmer';
import { useUI } from '../context/UIContext';
import {
    useListAboutTimelineQuery,
    useListAboutNumbersQuery,
    useListAboutTeamQuery,
    useListAboutAdvisoryQuery,
    useGetContactInfoQuery,
    useSubmitPartnershipInquiryMutation,
    useSubmitTrustianApplicationMutation,
} from '../store/siteContentApi';

const EMPTY_PARTNERSHIP_FORM = {
    name: '', phone: '', email: '', city: '', partnership_type: '', message: '',
};

const EMPTY_TRUSTIAN_FORM = {
    name: '', phone: '', email: '',
    dob: '', gender: '',
    city: '', address: '',
    qualification: '', experience: '',
    phlebotomy_certified: '', vehicle: '', driving_license: '',
    languages: '',
    why_join: '',
    consent: false,
};

const TRUSTIAN_CITIES = [
    'Hyderabad', 'Secunderabad', 'Warangal', 'Karimnagar', 'Khammam', 'Nizamabad',
    'Vijayawada', 'Visakhapatnam', 'Guntur', 'Tirupati', 'Rajahmundry', 'Kakinada',
    'Other',
];

const ACCENT_COLOR = {
    sage: 'var(--sage)',
    amber: 'var(--amber)',
    forest: 'var(--forest)',
};

// Normalise a WhatsApp number stored as "+919014638633" into a readable
// "90146 38633" for display, while preserving raw input if the format is
// unexpected (e.g. admin entered a different country code).
const formatWhatsapp = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) {
        const local = digits.slice(2);
        return `${local.slice(0, 5)} ${local.slice(5)}`;
    }
    return raw || '';
};

// ───────────────────────── Static content for sections we intentionally
// keep hardcoded — they rarely change (differentiators, accreditations,
// infra, trustian features). Timeline / Numbers / Team / Contact are
// admin-editable and fetched inside the component.
const DIFFERENTIATORS = [
    { ico: '🔬', t: 'NABL-Accredited Reference Lab', d: 'Your sample runs in our NABL-accredited central reference lab in Hyderabad', stat: 'ISO 15189', sl: 'National accreditation standard' },
    { ico: '🧠', t: 'Clinical Context in Every Report', d: 'Every biomarker comes with personalised clinical reasoning — what your value means for you, not a generic range from a textbook.', stat: '1,400+', sl: 'Biomarkers with clinical context' },
    { ico: '👨‍⚕️', t: 'Every Report Doctor-Verified', d: 'Pathologists review and co-sign every result before delivery. No algorithm acts alone.', stat: '100%', sl: 'Pathologist reviewed' },
    { ico: '📍 ', t: '14 Centres, One Standard', d: 'Walk into any of our 14 branches across Telangana and Andhra Pradesh', stat: '14', sl: 'Branches across Pan India' },
    { ico: '⚡', t: '60-Minute Service Promise', d: 'Book a home collection and your Trustian arrives within 60 minutes ', stat: '60 min', sl: 'From booking to arrival' },
    { ico: '💬', t: 'Humans on WhatsApp', d: 'A real medically-trained person responds in minutes — not a chatbot.', stat: 'Immediate', sl: 'WhatsApp response' },
];

const INFRA = [
    { ico: '🏭', t: 'Central Lab, Hyderabad', d: '30,000+ samples processed monthly on Beckman Coulter and other tier-1 analyzers running in parallel. ISO 15189:2022 certified.' },
    { ico: '🚚', t: 'Multi-Run Sample Logistics', d: '14 branches feed the central lab on scheduled cut-offs throughout the day — not a single overnight batch. Your sample doesn`t wait until tomorrow morning to enter the queue.' },

    { ico: '🔖', t: 'Barcoded Sample Accessioning', d: 'Every tube is barcoded the moment it`s drawn and scanned at every workstation. No manual re-entry, no misplaced racks, no delay searching for a sample tha`s "somewhere in chemistry."' },

    { ico: '⚙️', t: 'Parallel Analyzer Capacity ', d: 'Redundant high-throughput analyzers across chemistry, hematology, and immunoassay. One unit down doesn`t stall the queue — the next sample routes to the next available analyzer' },

    { ico: '🩺', t: 'Resident Pathologist Roster', d: 'Pathologists on-site through reporting hours — no samples sitting overnight waiting for the morning shift to verify and release.' },

    { ico: '📲', t: 'Auto-Release to Patient', d: 'The instant a pathologist signs off, the report pushes to WhatsApp, email, and the portal simultaneously. No human re-typing, no email queue, no "we`ll send it shortly."' },
];

const TRUSTIAN_FEATURES = [
    { ico: '✓', t: 'Background Verified', d: 'Police verification + address check before onboarding.' },
    { ico: '🎓', t: '6-Week Certification', d: 'Phlebotomy, cold chain, infection control and patient communication training.' },
    { ico: '🛡️', t: 'Cold Chain Equipment', d: 'IoT-monitored kits. Sample integrity logged from the moment of collection.' },
    { ico: '⭐', t: 'Rated by Every Patient', d: 'Sustained low ratings trigger re-training or removal — no exceptions.' },
];

// ───────────────────────── Reusable layout helpers ─────────────────────────
const SectionWrap = ({ id, bg = '#fff', children, dark = false }) => (
    <Box
        id={id}
        sx={{
            bgcolor: bg,
            py: { xs: 7, md: 11 },
            px: { xs: 2, md: 5 },
            color: dark ? '#fff' : 'inherit',
            // Offset so anchor scrolls don't hide the section header behind
            // the fixed navbar (≈64px xs / 80px md). Only applied when an id
            // is set, since non-anchored sections don't need it.
            ...(id ? { scrollMarginTop: { xs: 72, md: 96 } } : {}),
        }}
    >
        <Container maxWidth="xl" sx={{ maxWidth: 1300 }}>
            {children}
        </Container>
    </Box>
);

const NumberCard = ({ v, t, d }) => (
    <Box
        sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l)',
            borderRadius: '18px',
            p: { xs: 3, md: 3.5 },
            textAlign: 'center',
            height: '100%',
            transition: 'all 0.25s',
            '&:hover': {
                borderColor: 'var(--sage-p)',
                transform: 'translateY(-3px)',
                boxShadow: '0 10px 28px rgba(61,122,82,0.08)',
            },
        }}
    >
        <Typography
            className="serif"
            sx={{ fontSize: { xs: 38, md: 46 }, fontWeight: 600, color: 'var(--sage)', lineHeight: 1, mb: 1 }}
        >
            {v}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 0.5 }}>{t}</Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--stone)', fontWeight: 300 }}>{d}</Typography>
    </Box>
);

const FeatureCard = ({ ico, t, d, stat, sl }) => (
    <Box
        sx={{
            bgcolor: '#fff',
            border: '1px solid var(--border-l)',
            borderRadius: '20px',
            p: 3.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s',
            '&:hover': {
                borderColor: 'var(--sage-p)',
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(21,42,30,0.08)',
            },
        }}
    >
        <Typography sx={{ fontSize: 30, mb: 2, lineHeight: 1 }}>{ico}</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 1 }}>{t}</Typography>
        <Typography sx={{ fontSize: 13.5, color: 'var(--stone)', fontWeight: 300, lineHeight: 1.7, mb: 3, flex: 1 }}>
            {d}
        </Typography>
        <Box>
            <Typography className="serif" sx={{ fontSize: 30, fontWeight: 600, color: 'var(--sage)', lineHeight: 1, mb: 0.5 }}>
                {stat}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--stone)' }}>{sl}</Typography>
        </Box>
    </Box>
);

// Leadership bio dialog — opens from any "Read more" button in section
// 5 when a member's bio overflows the 2-line clamp on the card. The
// `member` prop is the same object the team card consumed, so we
// already have name / role / bio / avatar / colour and don't need a
// second fetch. Renders nothing while `member` is null so the dialog
// component stays mounted (no re-mount jank when reopening).
const TeamBioDialog = ({ member, onClose }) => {
    const open = !!member;
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Cream header strip — large avatar centred, name + role
          underneath. Matches the card visual so the dialog feels
          like an expansion of the tile, not a separate surface. */}
            <Box
                sx={{
                    position: 'relative',
                    px: { xs: 3, md: 4 },
                    pt: 4,
                    pb: 3,
                    textAlign: 'center',
                    background: 'linear-gradient(180deg, var(--cream) 0%, #fff 100%)',
                    borderBottom: '1px solid var(--border-l)',
                }}
            >
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: 'var(--stone)',
                        '&:hover': { color: 'var(--ink)', bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Box
                    sx={{
                        width: 96,
                        height: 96,
                        borderRadius: '50%',
                        bgcolor: member?.color || 'var(--sage)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 28,
                        fontWeight: 700,
                        mx: 'auto',
                        mb: 2,
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundImage: member?.avatar_url ? `url(${member.avatar_url})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: '0 12px 32px rgba(21,42,30,0.12)',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: -6,
                            borderRadius: '50%',
                            border: '1.5px solid var(--sage-p)',
                        },
                    }}
                >
                    {!member?.avatar_url && (member?.initials || '')}
                </Box>

                <Typography
                    className="serif"
                    sx={{
                        fontSize: { xs: 22, md: 26 },
                        fontWeight: 600,
                        color: 'var(--ink)',
                        lineHeight: 1.2,
                        mb: 0.75,
                    }}
                >
                    {member?.name}
                </Typography>
                <Typography
                    sx={{
                        fontSize: 11,
                        color: 'var(--sage)',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                    }}
                >
                    {member?.role}
                </Typography>
            </Box>

            {/* Body — full bio rendered in a comfortable reading column.
          `whiteSpace: pre-wrap` keeps any paragraph breaks the admin
          typed in instead of collapsing them. */}
            <Box
                sx={{
                    px: { xs: 3, md: 4 },
                    py: 3.5,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                }}
            >
                <Typography
                    // `!important` because the medical/lab portal classes inject a
                    // global rule that forces 'MedicalPortalSans' on every element
                    // under their body class — and if a user navigated from those
                    // portals straight to /about, the rule's specificity beats the
                    // theme. Forcing Outfit here guarantees the bio reads correctly
                    // regardless of where the user came from.
                    sx={{
                        fontFamily: "'Outfit', sans-serif !important",
                        fontSize: { xs: 15, md: 16 },
                        color: 'var(--ink)',
                        fontWeight: 400,
                        lineHeight: 1.8,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {member?.bio}
                </Typography>
            </Box>
        </Dialog>
    );
};

// Reusable input/select/textarea styling for the Trustian dialog. Light
// cream-on-white treatment — matches the rest of the About page surfaces.
const trustianFieldSx = {
    width: '100%',
    bgcolor: '#fff',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    p: '12px 14px',
    color: 'var(--ink)',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    '&::placeholder': { color: 'var(--stone)' },
    '&:focus': { borderColor: 'var(--sage)', boxShadow: '0 0 0 3px rgba(61,122,82,0.12)' },
};

const trustianSelectSx = { ...trustianFieldSx, appearance: 'none', cursor: 'pointer' };

const TrustianFieldLabel = ({ children }) => (
    <Typography
        sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--stone)',
            mb: 0.75,
        }}
    >
        {children}
    </Typography>
);

const TrustianApplyDialog = ({ open, onClose, form, setField, onSubmit, error, submitted, submitting, onReset }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
            sx: {
                borderRadius: '20px',
                bgcolor: '#fff',
                color: 'var(--ink)',
                overflow: 'hidden',
            },
        }}
    >
        {/* Header */}
        <Box
            sx={{
                position: 'relative',
                px: { xs: 3, md: 4 },
                pt: 4,
                pb: 3,
                borderBottom: '1px solid var(--border-l)',
                background: 'linear-gradient(180deg, var(--cream) 0%, #fff 100%)',
            }}
        >
            <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close"
                sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    color: 'var(--stone)',
                    '&:hover': { color: 'var(--ink)', bgcolor: 'rgba(0,0,0,0.04)' },
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Eyebrow>The Trustian Program</Eyebrow>
            <Typography
                className="serif"
                sx={{
                    color: 'var(--ink)',
                    fontSize: { xs: 22, md: 28 },
                    fontWeight: 600,
                    mt: 1.25,
                    mb: 1,
                    lineHeight: 1.2,
                }}
            >
                Apply to become a Trustian
            </Typography>
            <Typography
                sx={{
                    fontSize: 13.5,
                    color: 'var(--stone)',
                    fontWeight: 300,
                    lineHeight: 1.6,
                    maxWidth: 540,
                }}
            >
                Tell us about yourself. Shortlisted candidates are invited for a 6-week paid
                certification before joining the field team.
            </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 3.5 }, maxHeight: '70vh', overflowY: 'auto' }}>
            {submitted ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: 48, mb: 2 }}>🎉</Typography>
                    <Typography className="serif" sx={{ fontSize: 24, color: 'var(--ink)', fontWeight: 500, mb: 1.25 }}>
                        Application received.
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'var(--charcoal)', lineHeight: 1.7, maxWidth: 400, mx: 'auto', mb: 3 }}>
                        Our People team will review your details and reach out on the number you
                        provided within 5 working days.
                    </Typography>
                    <Stack direction="row" spacing={1.5} justifyContent="center">
                        <Button
                            onClick={onReset}
                            sx={{ color: 'var(--sage)', textTransform: 'none', fontSize: 13, fontWeight: 600 }}
                        >
                            Submit another application
                        </Button>
                        <Button
                            onClick={onClose}
                            variant="contained"
                            sx={{
                                bgcolor: 'var(--sage)',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '12px',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'var(--forest)' },
                            }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            ) : (
                <Box component="form" onSubmit={onSubmit} noValidate>
                    <Stack spacing={2.25}>
                        {error && (
                            <Alert
                                severity="error"
                                sx={{ borderRadius: '10px' }}
                            >
                                {error}
                            </Alert>
                        )}

                        {/* Name + Phone */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TrustianFieldLabel>Full Name *</TrustianFieldLabel>
                                <Box
                                    component="input"
                                    placeholder="As per ID proof"
                                    value={form.name}
                                    onChange={setField('name')}
                                    sx={trustianFieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TrustianFieldLabel>Mobile Number *</TrustianFieldLabel>
                                <Box
                                    component="input"
                                    type="tel"
                                    placeholder="+91 XXXXXXXXXX"
                                    value={form.phone}
                                    onChange={setField('phone')}
                                    sx={trustianFieldSx}
                                />
                            </Grid>
                        </Grid>

                        {/* Email + DOB */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TrustianFieldLabel>Email *</TrustianFieldLabel>
                                <Box
                                    component="input"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={form.email}
                                    onChange={setField('email')}
                                    sx={trustianFieldSx}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TrustianFieldLabel>Date of Birth</TrustianFieldLabel>
                                <Box
                                    component="input"
                                    type="date"
                                    value={form.dob}
                                    onChange={setField('dob')}
                                    sx={trustianFieldSx}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TrustianFieldLabel>Gender</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.gender}
                                    onChange={setField('gender')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select</option>
                                    <option value="male" style={{ color: '#333' }}>Male</option>
                                    <option value="female" style={{ color: '#333' }}>Female</option>
                                    <option value="other" style={{ color: '#333' }}>Other</option>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* City + Address */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={5}>
                                <TrustianFieldLabel>Preferred City *</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.city}
                                    onChange={setField('city')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select a city</option>
                                    {TRUSTIAN_CITIES.map((c) => (
                                        <option key={c} value={c} style={{ color: '#333' }}>{c}</option>
                                    ))}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TrustianFieldLabel>Current Locality / Area</TrustianFieldLabel>
                                <Box
                                    component="input"
                                    placeholder="e.g., Madhapur, Banjara Hills"
                                    value={form.address}
                                    onChange={setField('address')}
                                    sx={trustianFieldSx}
                                />
                            </Grid>
                        </Grid>

                        {/* Qualification + Experience */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TrustianFieldLabel>Highest Qualification *</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.qualification}
                                    onChange={setField('qualification')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select qualification</option>
                                    <option value="DMLT" style={{ color: '#333' }}>DMLT</option>
                                    <option value="Diploma in Phlebotomy" style={{ color: '#333' }}>Diploma in Phlebotomy</option>
                                    <option value="B.Sc MLT" style={{ color: '#333' }}>B.Sc MLT</option>
                                    <option value="B.Sc Nursing" style={{ color: '#333' }}>B.Sc Nursing</option>
                                    <option value="GNM / ANM" style={{ color: '#333' }}>GNM / ANM</option>
                                    <option value="12th / Intermediate" style={{ color: '#333' }}>12th / Intermediate</option>
                                    <option value="Other" style={{ color: '#333' }}>Other</option>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TrustianFieldLabel>Phlebotomy Experience *</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.experience}
                                    onChange={setField('experience')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select experience</option>
                                    <option value="fresher" style={{ color: '#333' }}>Fresher</option>
                                    <option value="0-1" style={{ color: '#333' }}>Less than 1 year</option>
                                    <option value="1-3" style={{ color: '#333' }}>1–3 years</option>
                                    <option value="3-5" style={{ color: '#333' }}>3–5 years</option>
                                    <option value="5+" style={{ color: '#333' }}>5+ years</option>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Certification + Vehicle + License */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TrustianFieldLabel>Phlebotomy Certified *</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.phlebotomy_certified}
                                    onChange={setField('phlebotomy_certified')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select</option>
                                    <option value="yes" style={{ color: '#333' }}>Yes</option>
                                    <option value="in_progress" style={{ color: '#333' }}>In progress</option>
                                    <option value="no" style={{ color: '#333' }}>No</option>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TrustianFieldLabel>Two-wheeler *</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.vehicle}
                                    onChange={setField('vehicle')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select</option>
                                    <option value="own" style={{ color: '#333' }}>Own</option>
                                    <option value="family" style={{ color: '#333' }}>Family vehicle</option>
                                    <option value="none" style={{ color: '#333' }}>None</option>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TrustianFieldLabel>Driving Licence</TrustianFieldLabel>
                                <Box
                                    component="select"
                                    value={form.driving_license}
                                    onChange={setField('driving_license')}
                                    sx={trustianSelectSx}
                                >
                                    <option value="" style={{ color: '#333' }}>Select</option>
                                    <option value="yes" style={{ color: '#333' }}>Yes</option>
                                    <option value="no" style={{ color: '#333' }}>No</option>
                                    <option value="learner" style={{ color: '#333' }}>Learner</option>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Languages */}
                        <Box>
                            <TrustianFieldLabel>Languages You Speak</TrustianFieldLabel>
                            <Box
                                component="input"
                                placeholder="e.g., Telugu, Hindi, English"
                                value={form.languages}
                                onChange={setField('languages')}
                                sx={trustianFieldSx}
                            />
                        </Box>

                        {/* Why join */}
                        <Box>
                            <TrustianFieldLabel>Why do you want to join TrustLab?</TrustianFieldLabel>
                            <Box
                                component="textarea"
                                rows={3}
                                placeholder="A few lines about your motivation, prior patient-care experience, etc."
                                value={form.why_join}
                                onChange={setField('why_join')}
                                sx={{ ...trustianFieldSx, minHeight: 90, resize: 'vertical' }}
                            />
                        </Box>

                        {/* Consent */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.25,
                                alignItems: 'flex-start',
                                bgcolor: 'var(--cream)',
                                border: '1px solid var(--border-l)',
                                p: 2,
                                borderRadius: '12px',
                            }}
                        >
                            <Box
                                component="input"
                                type="checkbox"
                                id="trustian-consent"
                                checked={form.consent}
                                onChange={setField('consent')}
                                sx={{ mt: '3px', accentColor: 'var(--sage)', cursor: 'pointer' }}
                            />
                            <Box
                                component="label"
                                htmlFor="trustian-consent"
                                sx={{
                                    flex: 1,
                                    fontSize: 12.5,
                                    color: 'var(--charcoal)',
                                    lineHeight: 1.55,
                                    cursor: 'pointer',
                                }}
                            >
                                I consent to TrustLab Diagnostics processing my personal data for the
                                purpose of evaluating my candidature, including police verification and
                                document checks. <Box component="span" sx={{ color: 'var(--sage)' }}>*</Box>
                            </Box>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                            sx={{
                                bgcolor: 'var(--sage)',
                                color: '#fff',
                                borderRadius: '14px',
                                textTransform: 'none',
                                fontWeight: 700,
                                py: 1.4,
                                '&:hover': { bgcolor: 'var(--forest)' },
                                '&.Mui-disabled': { bgcolor: 'var(--sage)', opacity: 0.6, color: '#fff' },
                            }}
                        >
                            {submitting ? 'Submitting…' : 'Submit Application →'}
                        </Button>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'var(--stone)',
                                textAlign: 'center',
                            }}
                        >
                            We respond to every application within 5 working days.
                        </Typography>
                    </Stack>
                </Box>
            )}
        </Box>
    </Dialog>
);

// One person card — shared by the Leadership and Advisory Board grids so
// both look identical. The avatar is an <img> layered over the initials:
// if the photo fails to load (dead/expired URL), onError hides it and the
// initials show through — so a broken URL never leaves a blank circle.
const MemberCard = ({ m, i, onReadMore }) => {
    const [imgOk, setImgOk] = useState(true);
    const showImg = !!m.avatar_url && imgOk;
    // Show "Read more" for any member that has a bio — the 2-line clamp
    // above may hide part of even a medium bio, and it keeps Leadership
    // and Advisory cards consistent.
    const isLong = (m.bio || '').trim().length > 0;
    return (
        <Grid item xs={12} sm={6} md={3}>
            <Reveal direction="up" delay={i * 80} sx={{ height: '100%' }}>
                <Box
                    sx={{
                        bgcolor: '#fff',
                        border: '1.5px solid var(--border-l)',
                        borderRadius: '18px',
                        p: 3,
                        height: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.25s',
                        '&:hover': {
                            borderColor: 'var(--sage-p)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 32px rgba(61,122,82,0.1)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: m.color,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 22,
                            fontWeight: 700,
                            mx: 'auto',
                            mb: 2.5,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: -5,
                                borderRadius: '50%',
                                border: '1.5px solid var(--sage-p)',
                            },
                        }}
                    >
                        {m.initials}
                        {showImg && (
                            <Box
                                component="img"
                                src={m.avatar_url}
                                alt={m.name}
                                onError={() => setImgOk(false)}
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        )}
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.5 }}>
                        {m.name}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 11, color: 'var(--sage)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.5 }}
                    >
                        {m.role}
                    </Typography>
                    {/* Bio clamped to 2 lines via -webkit-line-clamp; the dialog
                        is the source of truth for the full text. */}
                    <Typography
                        sx={{
                            fontSize: 12.5, color: 'var(--stone)',
                            lineHeight: 1.65, fontWeight: 300,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {m.bio}
                    </Typography>
                    {isLong && (
                        <Box sx={{ mt: 'auto', pt: 1.5 }}>
                            <Button
                                onClick={() => onReadMore(m)}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'var(--sage)',
                                    letterSpacing: '0.04em',
                                    px: 0,
                                    minWidth: 0,
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: 'var(--sage-p)',
                                    },
                                }}
                            >
                                Read more →
                            </Button>
                        </Box>
                    )}
                </Box>
            </Reveal>
        </Grid>
    );
};

const About = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { data: timeline = [], isLoading: timelineLoading } = useListAboutTimelineQuery();
    const { data: numbers = [], isLoading: numbersLoading } = useListAboutNumbersQuery();
    const { data: team = [], isLoading: teamLoading } = useListAboutTeamQuery();
    const { data: advisory = [], isLoading: advisoryLoading } = useListAboutAdvisoryQuery();

    // Anchor handling — when the URL has a hash like /about#leadership, scroll
    // to that section. Re-runs once `team` finishes loading so the leadership
    // section's real height (not the shimmer's) lands the scroll on target.
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);
        const el = document.getElementById(id);
        if (!el) return;
        // Wait one frame so layout settles after the data-driven section paints.
        requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }, [location.hash, teamLoading]);
    const { data: contact } = useGetContactInfoQuery();
    const { showToast } = useUI();
    const [submitPartnership, { isLoading: submittingPartnership }] = useSubmitPartnershipInquiryMutation();

    // Hero shows the first 4 numbers flagged is_hero_stat (by sort order).
    const heroStats = numbers.filter((n) => n.is_hero_stat).slice(0, 4);

    // Selected leadership member for the bio dialog. Null when closed.
    const [bioMember, setBioMember] = useState(null);

    // ─── Partnership enquiry form state ───
    const [pForm, setPForm] = useState(EMPTY_PARTNERSHIP_FORM);
    const [pError, setPError] = useState('');
    const [pSubmitted, setPSubmitted] = useState(false);
    const setPField = (k) => (e) => setPForm((f) => ({ ...f, [k]: e.target.value }));

    // ─── Trustian application form state (dialog launched from the
    // "Become a Trustian today" CTA in the Trustian Program section).
    const [trustianOpen, setTrustianOpen] = useState(false);
    const [tForm, setTForm] = useState(EMPTY_TRUSTIAN_FORM);
    const [tError, setTError] = useState('');
    const [tSubmitted, setTSubmitted] = useState(false);
    const [submitTrustianApplication, { isLoading: submittingTrustian }] = useSubmitTrustianApplicationMutation();
    const setTField = (k) => (e) => setTForm((f) => ({
        ...f,
        [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

    const onSubmitTrustian = async (e) => {
        e.preventDefault();
        setTError('');
        if (!tForm.name.trim()) return setTError('Please enter your full name');
        if (!tForm.phone.trim()) return setTError('Please enter your mobile number');
        if (!tForm.email.trim()) return setTError('Please enter your email');
        if (!/^\S+@\S+\.\S+$/.test(tForm.email.trim())) return setTError('Please enter a valid email address');
        if (!tForm.city) return setTError('Please select your preferred city');
        if (!tForm.qualification) return setTError('Please select your highest qualification');
        if (!tForm.experience) return setTError('Please select your experience level');
        if (!tForm.phlebotomy_certified) return setTError('Please tell us if you are phlebotomy certified');
        if (!tForm.vehicle) return setTError('Please tell us about your two-wheeler availability');
        if (!tForm.consent) return setTError('Please tick the consent box to continue');
        try {
            await submitTrustianApplication({
                name: tForm.name.trim(),
                phone: tForm.phone.trim(),
                email: tForm.email.trim(),
                dob: tForm.dob,
                gender: tForm.gender,
                city: tForm.city,
                address: tForm.address.trim(),
                qualification: tForm.qualification,
                experience: tForm.experience,
                phlebotomy_certified: tForm.phlebotomy_certified,
                vehicle: tForm.vehicle,
                driving_license: tForm.driving_license,
                languages: tForm.languages.trim(),
                why_join: tForm.why_join.trim(),
                consent: tForm.consent,
            }).unwrap();
            setTSubmitted(true);
        } catch (err) {
            setTError(err?.data?.message || 'Could not submit your application. Please try again.');
        }
        return undefined;
    };

    const onResetTrustian = () => {
        setTForm(EMPTY_TRUSTIAN_FORM);
        setTSubmitted(false);
        setTError('');
    };

    const onCloseTrustian = () => {
        setTrustianOpen(false);
        // Reset shortly after the close animation so the next open is fresh.
        setTimeout(() => onResetTrustian(), 200);
    };

    const onSubmitPartnership = async (e) => {
        e.preventDefault();
        setPError('');
        if (!pForm.name.trim()) return setPError('Please enter your name');
        if (!pForm.phone.trim()) return setPError('Please enter your mobile number');
        if (!pForm.email.trim()) return setPError('Please enter your email');
        if (!/^\S+@\S+\.\S+$/.test(pForm.email.trim())) return setPError('Please enter a valid email address');
        if (!pForm.city.trim()) return setPError('Please enter your city');
        if (!pForm.partnership_type) return setPError('Please select a partnership type');
        try {
            await submitPartnership({
                name: pForm.name.trim(),
                phone: pForm.phone.trim(),
                email: pForm.email.trim(),
                city: pForm.city.trim(),
                partnership_type: pForm.partnership_type,
                message: pForm.message.trim(),
            }).unwrap();
            setPSubmitted(true);
            setPForm(EMPTY_PARTNERSHIP_FORM);
            showToast?.('Thanks! Our partnerships team will reach out within 48 hours.');
        } catch (err) {
            setPError(err?.data?.message || 'Could not send — please try again.');
        }
    };

    // Contact channels reuse the shared ContactInfo singleton so the phone /
    // WhatsApp / email displayed here stay in sync with Support page, Footer,
    // and the floating CTA.
    const contactChannels = [
        {
            ico: '📞',
            t: 'Patient Support',
            sub: contact?.support_hours || 'Mon – Sat, 6 AM – 10 PM',
            val: contact?.phone || '74400 75400',
        },
        {
            ico: '💬',
            t: 'WhatsApp Support',
            sub: 'Response in under 5 minutes',
            val: formatWhatsapp(contact?.whatsapp || '+919014638633'),
        },
        {
            ico: '✉️',
            t: 'Email Us',
            sub: 'Support · Partnerships · Press',
            val: contact?.email || 'support@mytrustlab.com',
        },
    ];

    return (
        <Box>
            {/* ───────────── 1. HERO — reused from Home page ───────────── */}
            <HeroSection />

            {/* ───────────── 1b. ORIGINAL ABOUT HERO (hidden for now) ─────────────
                Wrapped in {false && (...)} so the markup stays in source for
                quick restoration. Flip to {true && (...)} or remove the guard
                to bring it back. */}
            {false && (
            <Box
                sx={{
                    bgcolor: 'var(--forest)',
                    color: '#fff',
                    pt: { xs: 5, md: 6 },
                    pb: { xs: 6, md: 8 },
                    px: { xs: 2, md: 5 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* subtle radial glow on the right edge */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(ellipse 60% 80% at 90% 40%, rgba(61,122,82,0.25) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />
                <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 1 }}>
                    {/* eyebrow */}
                    <Eyebrow white>About TrustLab</Eyebrow>

                    {/* headline */}
                    <Typography
                        className="serif"
                        component="h1"
                        sx={{
                            color: '#fff',
                            mt: 2,
                            mb: 2.5,
                            fontSize: { xs: 32, sm: 40, md: 48, lg: 54 },
                            fontWeight: 600,
                            lineHeight: 1.05,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Built on trust.
                        <Box
                            component="span"
                            sx={{
                                display: 'block',
                                fontStyle: 'italic',
                                color: 'var(--sage-l)',
                                fontWeight: 500,
                            }}
                        >
                            Driven by science.
                        </Box>
                    </Typography>

                    {/* description */}
                    <Typography
                        sx={{
                            fontSize: { xs: 15, md: 16 },
                            lineHeight: 1.8,
                            color: 'rgba(255,255,255,0.55)',
                            maxWidth: 560,
                            fontWeight: 300,
                            mb: { xs: 5, md: 6 },
                        }}
                    >
                        We started with one belief: that accurate diagnostics shouldn't require you to leave your home, stand in
                        queues, or wait three days for results. TrustLab is the answer we built.
                    </Typography>

                    {/* inline stats row */}
                    {numbersLoading ? (
                        <AboutHeroStatsShimmer count={4} />
                    ) : (
                        <Stack
                            direction="row"
                            spacing={{ xs: 4, sm: 6, md: 8 }}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ rowGap: 3 }}
                        >
                            {heroStats.map((n) => (
                                <Box key={n._id}>
                                    <Typography
                                        className="serif"
                                        sx={{
                                            fontSize: { xs: 34, md: 40 },
                                            fontWeight: 400,
                                            color: 'var(--sage-l)',
                                            lineHeight: 1,
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {n.value}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: 'rgba(255,255,255,0.4)',
                                            mt: 0.75,
                                            fontWeight: 400,
                                        }}
                                    >
                                        {n.title}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Container>
            </Box>
            )}

            {/* ───────────── 2. OUR STORY (with timeline) ───────────── */}
            <Box id="our-story" sx={{ bgcolor: '#fff', py: { xs: 7, md: 11 }, px: { xs: 2, md: 5 } }}>
                <Container maxWidth="xl" sx={{ maxWidth: 1300 }}>
                    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="stretch">
                        {/* ── LEFT: eyebrow + heading + 3 paragraphs ── */}
                        <Grid item xs={12} md={6}>
                            <Reveal direction="left">
                                <Eyebrow>Our Story</Eyebrow>
                                <Typography
                                    className="serif"
                                    component="h2"
                                    sx={{
                                        mt: 2,
                                        mb: 3.5,
                                        fontSize: { xs: 32, sm: 38, md: 44, lg: 48 },
                                        fontWeight: 500,
                                        lineHeight: 1.15,
                                        letterSpacing: '-0.015em',
                                        color: 'var(--ink)',
                                    }}
                                >
                                    Built so testing stops being a reaction.
                                </Typography>
                                <Stack spacing={3}>
                                    <Typography sx={{ fontSize: 15, lineHeight: 1.85, color: 'var(--charcoal)', fontWeight: 300 }}>
                                        When a person walks into a diagnostic laboratory, they bring with them something far more fragile than
                                        a sample for testing. They bring trust — the quiet, often unspoken belief that the people behind the
                                        laboratory door will give them an honest answer about their own body. It is a form of trust unlike any
                                        other in daily life. It cannot be insured, refunded, or undone. It can only be honoured.
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, lineHeight: 1.85, color: 'var(--charcoal)', fontWeight: 300 }}>
                                        TrustLab was founded on the recognition of that responsibility. We exist to meet this trust with the
                                        seriousness it deserves — through accuracy that is never approximated, processes that are never
                                        shortened, and a refusal to treat any report as routine. Behind every result is a patient waiting for
                                        clarity, and clarity is what we owe them.
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, lineHeight: 1.85, color: 'var(--charcoal)', fontWeight: 300 }}>
                                        This conviction has shaped every decision we have made. We have invested in advanced diagnostic
                                        platforms, built rigorous quality systems, and assembled a team of qualified professionals whose
                                        competence is matched by their care. Our test menu has expanded steadily, our protocols have been
                                        refined continually, and our standards have moved in only one direction — upward.
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, lineHeight: 1.85, color: 'var(--charcoal)', fontWeight: 300 }}>
                                        From a single laboratory in Hyderabad, TrustLab has grown to fourteen branches across India, each
                                        equipped to deliver the same standard of testing that defined us on day one. Whether you are a
                                        clinician, a patient, or someone simply choosing to stay ahead of your health, TrustLab is built to
                                        be where you need it, when you need it.
                                    </Typography>
                                </Stack>
                            </Reveal>
                        </Grid>

                        {/* ── RIGHT: vertical timeline ── */}
                        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                            <Reveal direction="right" delay={120} sx={{ flex: 1, display: 'flex' }}>
                                {timelineLoading ? (
                                    <AboutTimelineShimmer count={4} />
                                ) : (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            pl: { xs: 3, md: 3.5 },
                                            borderLeft: '2px solid var(--sage-p)',
                                            mt: { xs: 0, md: 1 },
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        {timeline.map((t, i) => {
                                            const ringColor = ACCENT_COLOR[t.accent] || 'var(--sage)';
                                            return (
                                                <Box
                                                    key={t._id || t.year}
                                                    sx={{
                                                        position: 'relative',
                                                        // Flex space-between handles the desktop spacing; keep
                                                        // a fallback gap on mobile where the column width is
                                                        // narrower and the height auto-fits the content.
                                                        mb: i === timeline.length - 1 ? 0 : { xs: 4, md: 0 },
                                                    }}
                                                >
                                                    {/* Hollow ring marker centered on the vertical line */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            left: { xs: '-34px', md: '-38px' },
                                                            top: '2px',
                                                            width: 18,
                                                            height: 18,
                                                            bgcolor: '#fff',
                                                            border: `2.5px solid ${ringColor}`,
                                                            borderRadius: '50%',
                                                            boxSizing: 'border-box',
                                                        }}
                                                    />

                                                    {/* Year */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            letterSpacing: '0.08em',
                                                            color: ringColor,
                                                            mb: 0.75,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {t.year}
                                                    </Typography>

                                                    {/* Title */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: 16,
                                                            fontWeight: 700,
                                                            color: 'var(--ink)',
                                                            mb: 1,
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {t.title}
                                                    </Typography>

                                                    {/* Description */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13.5,
                                                            color: 'var(--stone)',
                                                            fontWeight: 300,
                                                            lineHeight: 1.7,
                                                        }}
                                                    >
                                                        {t.description}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ───────────── 3. MISSION & VISION ───────────── */}
            <Box sx={{ bgcolor: 'var(--forest)', py: { xs: 7, md: 11 }, px: { xs: 2, md: 5 }, position: 'relative', overflow: 'hidden' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(ellipse 70% 80% at 80% 50%, rgba(61,122,82,0.18) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }}
                />
                <Container maxWidth="xl" sx={{ maxWidth: 1000, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 3, md: 3 }}>
                        {[
                            {
                                ico: '🎯',
                                eyebrow: 'Our Mission',
                                title: "Make rigorous diagnostics part of every Indian family's routine.",
                                body: "A blood test shouldn't be reserved for emergencies or annual checkups. We bring the lab to your door, deliver reports in hours not days, and stand behind every result with a pathologist's signature — so testing becomes something families do twice a year, not once in a crisis.",
                                accent: 'sage',
                            },
                            {
                                ico: '🔭',
                                eyebrow: 'Our Vision',
                                title: 'Bring world-class diagnostics within reach of every Indian family.',
                                body: "Knowing your health numbers shouldn't depend on where you live, what you earn, or how much time you can spare. We're building the infrastructure to make rigorous testing a routine choice for every family — so silent diseases stop staying silent, and the most dangerous conditions are caught early, when they're still easy to treat.",
                                accent: 'amber',
                            },
                        ].map((card, i) => (
                            <Grid key={card.eyebrow} item xs={12} md={6}>
                                <Reveal direction={i === 0 ? 'left' : 'right'} delay={i * 120} sx={{ height: '100%' }}>
                                    <Box
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.04)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '20px',
                                            p: { xs: 3, md: 4 },
                                            height: '100%',
                                            transition: 'all 0.3s',
                                            '&:hover': { borderColor: 'rgba(200,237,216,0.3)', bgcolor: 'rgba(255,255,255,0.06)' },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 46,
                                                height: 46,
                                                bgcolor: card.accent === 'amber' ? 'rgba(201,125,8,0.15)' : 'rgba(61,122,82,0.25)',
                                                border: `1.5px solid ${card.accent === 'amber' ? 'rgba(201,125,8,0.3)' : 'rgba(61,122,82,0.4)'}`,
                                                borderRadius: '13px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 22,
                                                mb: 2.5,
                                            }}
                                        >
                                            {card.ico}
                                        </Box>
                                        <Eyebrow white>{card.eyebrow}</Eyebrow>
                                        <Typography
                                            className="serif"
                                            sx={{ color: '#fff', fontSize: { xs: 22, md: 26 }, fontWeight: 600, lineHeight: 1.2, mt: 1.5, mb: 2 }}
                                        >
                                            {card.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontWeight: 300 }}>
                                            {card.body}
                                        </Typography>
                                    </Box>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ───────────── 4. BY THE NUMBERS ───────────── */}
            <SectionWrap bg="var(--parchment)">
                <Reveal direction="up">
                    <SectionHeader
                        eyebrow="By The Numbers"
                        title="TrustLab at a glance"
                        align="center"
                    />
                </Reveal>
                {numbersLoading ? (
                    <AboutNumbersShimmer count={8} />
                ) : (
                    <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
                        {numbers.map((n, i) => (
                            <Grid key={n._id || n.title} item xs={6} md={4}>
                                <Reveal direction="up" delay={i * 70}>
                                    <NumberCard v={n.value} t={n.title} d={n.description} />
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </SectionWrap>

            {/* ───────────── 5. LEADERSHIP TEAM ───────────── */}
            <SectionWrap id="leadership" bg="#fff">
                <Reveal direction="up">
                    <SectionHeader
                        eyebrow="Leadership"
                        title="The people behind TrustLab"
                        description="Two people who built TrustLab because they kept watching Indian families get diagnosed too late — and decided to build the lab they wished their own families had."
                        align="center"
                    />
                </Reveal>
                {teamLoading ? (
                    <AboutTeamShimmer count={4} />
                ) : (
                    <Grid
                        container
                        spacing={{ xs: 2, md: 2.5 }}
                        justifyContent="center"
                        sx={{ mt: { xs: 3, md: 4 } }}
                    >
                        {team.map((m, i) => (
                            <MemberCard key={m._id || m.initials} m={m} i={i} onReadMore={setBioMember} />
                        ))}
                    </Grid>
                )}
            </SectionWrap>

            {/* ───────────── 5b. ADVISORY BOARD ───────────── */}
            {/* Same card design as Leadership. Hidden entirely when there
                are no active advisory members so the page never shows an
                empty section. */}
            {(advisoryLoading || advisory.length > 0) && (
                <SectionWrap id="advisory" bg="var(--cream)">
                    <Reveal direction="up">
                        <SectionHeader
                            eyebrow="Advisory Board"
                            title="The experts who guide us"
                            description="Senior clinicians and operators who advise TrustLab on clinical strategy, quality governance and responsible growth."
                            align="center"
                        />
                    </Reveal>
                    {advisoryLoading ? (
                        <AboutTeamShimmer count={4} />
                    ) : (
                        <Grid
                            container
                            spacing={{ xs: 2, md: 2.5 }}
                            justifyContent="center"
                            sx={{ mt: { xs: 3, md: 4 } }}
                        >
                            {advisory.map((m, i) => (
                                <MemberCard key={m._id || m.initials} m={m} i={i} onReadMore={setBioMember} />
                            ))}
                        </Grid>
                    )}
                </SectionWrap>
            )}

            {/* Bio dialog — opened from any leadership card whose bio is too
        long for the 2-line clamp. Mounted once at the page level so
        the dialog survives card re-renders. */}
            <TeamBioDialog member={bioMember} onClose={() => setBioMember(null)} />

            {/* Trustian application dialog — opened from "Become a Trustian today"
                CTA in the Trustian Program section. UI only; no backend yet. */}
            <TrustianApplyDialog
                open={trustianOpen}
                onClose={onCloseTrustian}
                form={tForm}
                setField={setTField}
                onSubmit={onSubmitTrustian}
                error={tError}
                submitted={tSubmitted}
                submitting={submittingTrustian}
                onReset={onResetTrustian}
            />

            {/* ───────────── 6. OUR MEDICAL TEAM ───────────── */}
            <Box sx={{ bgcolor: 'var(--forest)', py: { xs: 7, md: 11 }, px: { xs: 2, md: 5 }, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 50% 60% at 20% 50%, rgba(61,122,82,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Reveal direction="left">
                                <Eyebrow white>Our Medical Team</Eyebrow>
                                <Typography className="serif" component="h2" sx={{ color: '#fff', fontSize: { xs: 28, md: 38 }, fontWeight: 600, mt: 2, mb: 2.5, lineHeight: 1.15 }}>
                                    Every report has a doctor behind it.
                                </Typography>
                                <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', fontWeight: 300, lineHeight: 1.8, mb: 3.5 }}>
                                    No algorithm alone ever releases a report at TrustLab. Every result is reviewed and signed by one of our 20+ in-house pathologists before it reaches you. This is non-negotiable.
                                </Typography>
                                <Stack spacing={2.5}>
                                    {[
                                        { ico: '🔬', t: '20+ MD Pathologists on staff', d: 'Trained at AIIMS, CMC, NIMHANS and other premier institutions' },
                                        { ico: '🤖', t: 'AI flags, humans decide', d: 'Our AI pre-screens for anomalies and highlights concerns for pathologist review' },
                                        { ico: '💬', t: 'Clinical context in every report', d: 'Abnormal values come with doctor-written explanations and next-step guidance' },
                                    ].map((item) => (
                                        <Box key={item.t} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                            <Box sx={{ width: 44, height: 44, bgcolor: 'rgba(61,122,82,0.25)', border: '1.5px solid rgba(61,122,82,0.4)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                                                {item.ico}
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff', mb: 0.5 }}>{item.t}</Typography>
                                                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontWeight: 300 }}>{item.d}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Reveal direction="right" delay={120}>
                                <Grid container spacing={1.75}>
                                    {[
                                        { v: '20+', t: 'Pathologists', d: 'In-house medical staff' },
                                        { v: '100%', t: 'Reports Reviewed', d: 'No algorithm releases alone' },
                                        { v: '6 hrs', t: 'Turnaround Time', d: 'For routine tests' },
                                        { v: '<30 min', t: 'Critical Value Alerts', d: 'Direct pathologist call before release' },
                                    ].map((s) => (
                                        <Grid key={s.t} item xs={6}>
                                            <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', p: 2.75, textAlign: 'center' }}>
                                                <Typography className="serif" sx={{ fontSize: 42, color: 'var(--sage-l)', fontWeight: 400, lineHeight: 1, mb: 0.75 }}>{s.v}</Typography>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', mb: 0.5 }}>{s.t}</Typography>
                                                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{s.d}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ───────────── 7. WHAT MAKES US DIFFERENT ───────────── */}
            <SectionWrap bg="var(--cream)">
                <Reveal direction="up">
                    <SectionHeader
                        eyebrow="What Makes Us Different"
                        title="Science-backed. Human-first."
                        description="Rigorous enough for the test. Warm enough for the family."
                        align="center"
                    />
                </Reveal>
                <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
                    {DIFFERENTIATORS.map((f, i) => (
                        <Grid key={f.t} item xs={12} sm={6} md={4}>
                            <Reveal direction="up" delay={(i % 3) * 90} sx={{ height: '100%' }}>
                                <FeatureCard {...f} />
                            </Reveal>
                        </Grid>
                    ))}
                </Grid>
            </SectionWrap>

            {/* ───────────── 7. ACCREDITATIONS ───────────── */}
            {/* <SectionWrap bg="var(--parchment)">
                <Reveal direction="up">
                    <SectionHeader
                        eyebrow="Accreditations & Certifications"
                        title="Held to the highest standard."
                        description="Every accreditation here represents a rigorous external audit — not a self-declaration."
                        align="center"
                    />
                </Reveal>
                <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
                    {ACCREDITATIONS.map((a, i) => (
                        <Grid key={a.t} item xs={12} sm={6} md={4}>
                            <Reveal direction="up" delay={(i % 3) * 90} sx={{ height: '100%' }}>
                                <Box
                                    sx={{
                                        bgcolor: '#fff',
                                        border: '1px solid var(--border-l)',
                                        borderRadius: '18px',
                                        p: 3,
                                        height: '100%',
                                        display: 'flex',
                                        gap: 2.5,
                                        alignItems: 'flex-start',
                                        transition: 'all 0.25s',
                                        '&:hover': { borderColor: 'var(--sage-p)', transform: 'translateY(-3px)' },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            bgcolor: 'var(--mint)',
                                            border: '1.5px solid var(--sage-p)',
                                            borderRadius: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 22,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {a.ico}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', mb: 0.75 }}>{a.t}</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 300, lineHeight: 1.65 }}>
                                            {a.d}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Reveal>
                        </Grid>
                    ))}
                </Grid>
            </SectionWrap> */}

            {/* ───────────── 8. INFRASTRUCTURE ───────────── */}
            <SectionWrap bg="#fff">
                <SectionHeader
                    eyebrow="Our Infrastructure"
                    title="Built for accuracy at scale."
                    description="Behind every 6-hour report is a laboratory infrastructure built to handle 50,000+ samples monthly without compromising on a single result."
                    align="center"
                />
                <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
                    {INFRA.map((f, i) => (
                        <Grid key={f.t} item xs={12} sm={6} md={4}>
                            <Reveal direction="up" delay={(i % 3) * 90} sx={{ height: '100%' }}>
                                <Box
                                    sx={{
                                        bgcolor: 'var(--cream)',
                                        border: '1px solid var(--border-l)',
                                        borderRadius: '18px',
                                        p: 3.5,
                                        height: '100%',
                                        transition: 'all 0.3s',
                                        '&:hover': { borderColor: 'var(--sage-p)', boxShadow: '0 10px 30px rgba(21,42,30,0.06)' },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 28, mb: 2 }}>{f.ico}</Typography>
                                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 1 }}>{f.t}</Typography>
                                    <Typography sx={{ fontSize: 13.5, color: 'var(--stone)', fontWeight: 300, lineHeight: 1.7 }}>
                                        {f.d}
                                    </Typography>
                                </Box>
                            </Reveal>
                        </Grid>
                    ))}
                </Grid>
            </SectionWrap>

            {/* ───────────── 9. THE TRUSTIAN PROGRAM ───────────── */}
            <Box sx={{ bgcolor: 'var(--forest)', py: { xs: 7, md: 11 }, px: { xs: 2, md: 5 }, position: 'relative', overflow: 'hidden' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -200,
                        right: -200,
                        width: 500,
                        height: 500,
                        background: 'radial-gradient(circle, rgba(61,122,82,0.18) 0%, transparent 70%)',
                        borderRadius: '50%',
                        pointerEvents: 'none',
                    }}
                />
                <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <Reveal direction="left">
                                <Eyebrow white>The Trustian Program</Eyebrow>
                                <Typography className="serif" sx={{ color: '#fff', fontSize: { xs: 28, md: 38 }, fontWeight: 600, mt: 2, mb: 2.5, lineHeight: 1.15 }}>
                                    Not just a phlebotomist.<br />A Trustian.
                                </Typography>
                                <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', fontWeight: 300, lineHeight: 1.8, mb: 4 }}>
                                    Every field agent at TrustLab goes through a rigorous 6-week certification before they ever visit a
                                    patient's home. They're trained not just in phlebotomy but in patient experience, infection control,
                                    cold-chain handling, and clinical communication.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={() => setTrustianOpen(true)}
                                >
                                    Become a Trustian today →
                                </Button>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Reveal direction="right" delay={120}>
                                <Grid container spacing={2}>
                                    {TRUSTIAN_FEATURES.map((f, i) => (
                                        <Grid key={f.t} item xs={12} sm={6}>
                                            <Box
                                                sx={{
                                                    bgcolor: i === 3 ? 'rgba(201,125,8,0.1)' : 'rgba(255,255,255,0.05)',
                                                    border: i === 3 ? '1.5px solid rgba(201,125,8,0.3)' : '1px solid rgba(255,255,255,0.1)',
                                                    borderRadius: '16px',
                                                    p: 2.75,
                                                    height: '100%',
                                                    display: 'flex',
                                                    gap: 2,
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: i === 3 ? 'rgba(201,125,8,0.2)' : 'rgba(61,122,82,0.25)',
                                                        border: i === 3 ? '1.5px solid rgba(201,125,8,0.4)' : '1.5px solid rgba(61,122,82,0.4)',
                                                        borderRadius: '11px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 16,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {f.ico}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff', mb: 0.5 }}>{f.t}</Typography>
                                                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 300, lineHeight: 1.6 }}>
                                                        {f.d}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ───────────── 10. PARTNERSHIPS & FRANCHISES ───────────── */}
            <Box sx={{ bgcolor: 'var(--ink, #161612)', py: { xs: 7, md: 11 }, px: { xs: 2, md: 5 }, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -180, right: -180, width: 520, height: 520, background: 'radial-gradient(circle, rgba(201,125,8,0.12) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -120, left: -120, width: 400, height: 400, background: 'radial-gradient(circle, rgba(61,122,82,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
                <Container maxWidth="xl" sx={{ maxWidth: 1300, position: 'relative', zIndex: 1 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7.5 } }}>
                        <Eyebrow white>Partnerships & Franchises</Eyebrow>
                        <Typography className="serif" component="h2" sx={{ color: '#fff', fontSize: { xs: 28, md: 38 }, fontWeight: 600, mt: 2, mb: 2, lineHeight: 1.15 }}>
                            Grow with TrustLab.{' '}
                            <Box component="em" sx={{ fontStyle: 'italic', color: 'var(--sage-l)' }}>Bring diagnostics to your city.</Box>
                        </Typography>
                        <Typography sx={{ fontSize: 17, color: 'rgba(255,255,255,0.45)', fontWeight: 300, lineHeight: 1.8, maxWidth: 580, mx: 'auto' }}>
                            Whether you're a clinic, hospital, existing lab, or entrepreneur — TrustLab's franchise and partnership model gives you a proven brand, full operational support, and access to 1700+ tests from day one.
                        </Typography>
                    </Box>

                    {/* 3 Partnership Models */}
                    <Grid container spacing={2.5} sx={{ mb: { xs: 5, md: 7.5 } }}>
                        {[
                            {
                                ico: '🏪', accent: 'amber', label: 'Collection Centre Franchise',
                                title: 'Own a TrustLab centre in your locality',
                                desc: 'Set up a branded TrustLab collection centre with our full support — equipment, training, LIMS access, Trustian network integration, and marketing.',
                                perks: ['Full brand & signage support', 'Access to 2,000+ tests', 'NABL lab processing included', 'Revenue sharing model', '6-week setup & training'],
                                footer: 'Investment: ₹5L – ₹15L · ROI typically 18–24 months',
                            },
                            {
                                ico: '🏥', accent: 'sage', label: 'Hospital & Clinic Partnership', popular: true,
                                title: 'Add TrustLab diagnostics to your practice',
                                desc: "Integrate TrustLab's home collection network into your clinic. Refer patients directly — we handle collection, lab processing, and report delivery.",
                                perks: ['Zero capital investment', 'Direct referral integration', 'Co-branded patient reports', 'Referral commission on every test', 'API & EMR integration available'],
                                footer: 'Setup: Live in 7 days · No minimum volumes',
                            },
                            {
                                ico: '🌍', accent: 'sage-l', label: 'City Master Franchise',
                                title: 'Own TrustLab operations across an entire city',
                                desc: 'Exclusive rights to operate and expand TrustLab in your city — manage multiple centres, build a Trustian fleet, and benefit from city-wide marketing.',
                                perks: ['Exclusive city territory rights', 'Own & sub-franchise centres', 'Full ops team secondment', 'Dedicated BD & marketing budget', 'Equity consideration for top cities'],
                                footer: 'Investment: ₹25L – ₹75L · Limited cities available',
                            },
                        ].map((model) => {
                            const isAmber = model.accent === 'amber';
                            const accentColor = isAmber ? 'rgba(201,125,8,0.3)' : 'rgba(61,122,82,0.35)';
                            const accentBg = isAmber ? 'rgba(201,125,8,0.15)' : 'rgba(61,122,82,0.25)';
                            const labelColor = isAmber ? 'var(--amber)' : 'var(--sage-l)';
                            const tickColor = isAmber ? 'var(--amber)' : 'var(--sage-l)';
                            return (
                                <Grid key={model.label} item xs={12} md={4}>
                                    <Box sx={{
                                        bgcolor: model.popular ? 'rgba(61,122,82,0.08)' : 'rgba(255,255,255,0.04)',
                                        border: model.popular ? '1.5px solid rgba(61,122,82,0.35)' : '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '24px', p: { xs: 3.5, md: 4.5 }, height: '100%', position: 'relative',
                                        transition: 'all 0.3s',
                                        '&:hover': { borderColor: accentColor, bgcolor: model.popular ? 'rgba(61,122,82,0.14)' : 'rgba(255,255,255,0.06)' },
                                    }}>
                                        {model.popular && (
                                            <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'var(--sage)', color: '#fff', fontSize: 10, fontWeight: 700, px: 1.5, py: 0.5, borderRadius: '100px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                                Most Popular
                                            </Box>
                                        )}
                                        <Box sx={{ width: 54, height: 54, bgcolor: accentBg, border: `1.5px solid ${accentColor}`, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, mb: 2.75 }}>
                                            {model.ico}
                                        </Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: labelColor, mb: 1.25 }}>{model.label}</Typography>
                                        <Typography className="serif" sx={{ fontSize: 22, color: '#fff', fontWeight: 400, mb: 1.75, lineHeight: 1.2 }}>{model.title}</Typography>
                                        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontWeight: 300, lineHeight: 1.75, mb: 3 }}>{model.desc}</Typography>
                                        <Stack spacing={1.25} sx={{ mb: 3.5 }}>
                                            {model.perks.map((p) => (
                                                <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                                                    <Typography sx={{ color: tickColor, fontWeight: 700 }}>✓</Typography> {p}
                                                </Box>
                                            ))}
                                        </Stack>
                                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '12px', p: 1.75, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                                            <Box component="span" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>{model.footer.split('·')[0]}·</Box>
                                            {model.footer.split('·').slice(1).join('·')}
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Why Partner With Us */}
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '24px', p: { xs: 4, md: 5.5 }, mb: { xs: 5, md: 7.5 } }}>
                        <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <Eyebrow white>Why Partner With TrustLab</Eyebrow>
                            <Typography className="serif" sx={{ fontSize: { xs: 22, md: 30 }, color: '#fff', fontWeight: 400, mt: 1.5 }}>
                                Everything you need to succeed. Nothing held back.
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            {[
                                { ico: '🏅', t: 'NABL Brand Credibility', d: 'Patients trust NABL. You inherit that trust on day one — no years of brand-building required.' },
                                { ico: '🔬', t: '1700+ Tests Ready', d: 'No need to build lab infrastructure. Access our full test menu via our LIMS from day one.' },
                                { ico: '📱', t: 'Technology Provided', d: 'Booking app, GPS tracking, LIMS, WhatsApp reports — fully built, constantly updated, yours to use.' },
                                { ico: '📣', t: 'Marketing Support', d: 'Centralised digital marketing, city-level campaigns, Google My Business support, and co-branded materials.' },
                            ].map((item) => (
                                <Grid key={item.t} item xs={12} sm={6} md={3}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 32, mb: 1.5 }}>{item.ico}</Typography>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#fff', mb: 0.75 }}>{item.t}</Typography>
                                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', fontWeight: 300, lineHeight: 1.65 }}>{item.d}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Contact Row */}
                    <Grid container spacing={{ xs: 5, md: 7.5 }} alignItems="flex-start">
                        <Grid item xs={12} md={6}>
                            <Eyebrow white>Get In Touch</Eyebrow>
                            <Typography className="serif" sx={{ fontSize: { xs: 24, md: 34 }, color: '#fff', fontWeight: 400, mt: 1.5, mb: 2, lineHeight: 1.2 }}>
                                Ready to bring TrustLab to your city?
                            </Typography>
                            <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)', fontWeight: 300, lineHeight: 1.8, mb: 4 }}>
                                Fill in the form and our partnerships team will reach out within 48 hours. We'll walk you through the model that fits your situation — no pressure, just a conversation.
                            </Typography>
                            <Stack spacing={2}>
                                {[
                                    { ico: '📞', t: 'Call our Partnerships Team', v: contact?.phone || '74400 75400' },
                                    { ico: '✉️', t: 'Email us directly', v: 'partnerships@mytrustlab.com' },
                                    { ico: '💬', t: 'WhatsApp us', v: formatWhatsapp(contact?.whatsapp) || '90146 38633' },
                                ].map((ch) => (
                                    <Box key={ch.t} sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
                                        <Box sx={{ width: 40, height: 40, bgcolor: 'rgba(61,122,82,0.2)', border: '1px solid rgba(61,122,82,0.3)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{ch.ico}</Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{ch.t}</Typography>
                                            <Typography className="mono" sx={{ fontSize: 14, color: 'var(--sage-l)', mt: 0.25 }}>{ch.v}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component="form"
                                onSubmit={onSubmitPartnership}
                                sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', p: { xs: 3.5, md: 4.5 } }}
                            >
                                {pSubmitted ? (
                                    /* ── Thank-you state ── */
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography sx={{ fontSize: 48, mb: 2 }}>🤝</Typography>
                                        <Typography className="serif" sx={{ fontSize: 22, color: '#fff', fontWeight: 500, mb: 1.25 }}>
                                            Got it — talk soon.
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, maxWidth: 360, mx: 'auto', mb: 3 }}>
                                            Our partnerships team will reach out to you within 48 hours on the number you provided.
                                        </Typography>
                                        <Button
                                            onClick={() => setPSubmitted(false)}
                                            sx={{ color: 'var(--sage-l)', textTransform: 'none', fontSize: 13, fontWeight: 600 }}
                                        >
                                            Send another enquiry
                                        </Button>
                                    </Box>
                                ) : (
                                    <Stack spacing={2.25}>
                                        {pError && (
                                            <Alert severity="error" sx={{ borderRadius: '10px', bgcolor: 'rgba(220,38,38,0.12)', color: '#FCA5A5', '& .MuiAlert-icon': { color: '#FCA5A5' } }}>
                                                {pError}
                                            </Alert>
                                        )}
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>Your Name *</Typography>
                                            <Box
                                                component="input"
                                                placeholder="Full name"
                                                value={pForm.name}
                                                onChange={setPField('name')}
                                                disabled={submittingPartnership}
                                                sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', '&::placeholder': { color: 'rgba(255,255,255,0.25)' } }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>Mobile Number *</Typography>
                                            <Box
                                                component="input"
                                                type="tel"
                                                placeholder="+91 XXXXXXXXXX"
                                                value={pForm.phone}
                                                onChange={setPField('phone')}
                                                disabled={submittingPartnership}
                                                sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', '&::placeholder': { color: 'rgba(255,255,255,0.25)' } }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>Email *</Typography>
                                            <Box
                                                component="input"
                                                type="email"
                                                placeholder="name@company.com"
                                                value={pForm.email}
                                                onChange={setPField('email')}
                                                disabled={submittingPartnership}
                                                sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', '&::placeholder': { color: 'rgba(255,255,255,0.25)' } }}
                                            />
                                        </Box>
                                        <Grid container spacing={1.75}>
                                            <Grid item xs={6}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>City *</Typography>
                                                <Box
                                                    component="input"
                                                    placeholder="Your city"
                                                    value={pForm.city}
                                                    onChange={setPField('city')}
                                                    disabled={submittingPartnership}
                                                    sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', '&::placeholder': { color: 'rgba(255,255,255,0.25)' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>Partnership Type *</Typography>
                                                <Box
                                                    component="select"
                                                    value={pForm.partnership_type}
                                                    onChange={setPField('partnership_type')}
                                                    disabled={submittingPartnership}
                                                    sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', appearance: 'none' }}
                                                >
                                                    <option value="" style={{ color: '#333' }}>Select type</option>
                                                    <option value="Collection Centre" style={{ color: '#333' }}>Collection Centre</option>
                                                    <option value="Hospital / Clinic" style={{ color: '#333' }}>Hospital / Clinic</option>
                                                    <option value="City Master Franchise" style={{ color: '#333' }}>City Master Franchise</option>
                                                    <option value="Other" style={{ color: '#333' }}>Other</option>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Box>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 0.75 }}>Tell us about yourself</Typography>
                                            <Box
                                                component="textarea"
                                                rows={3}
                                                placeholder="Current profession, why you're interested, any questions..."
                                                value={pForm.message}
                                                onChange={setPField('message')}
                                                disabled={submittingPartnership}
                                                sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px', p: '12px 16px', color: '#fff', fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 80, fontFamily: 'inherit', '&::placeholder': { color: 'rgba(255,255,255,0.25)' } }}
                                            />
                                        </Box>
                                        <Button
                                            type="submit"
                                            variant="contained" size="large"
                                            disabled={submittingPartnership}
                                            startIcon={submittingPartnership ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                                            sx={{ bgcolor: 'var(--amber)', color: '#fff', borderRadius: '14px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#B45309' } }}
                                        >
                                            {submittingPartnership ? 'Sending…' : 'Send Partnership Enquiry →'}
                                        </Button>
                                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>Our partnerships team responds within 48 hours</Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ───────────── 11. CAREERS BANNER ───────────── */}
            <Box sx={{ bgcolor: 'var(--sage)', py: { xs: 7, md: 9 }, px: { xs: 2, md: 5 }, position: 'relative', overflow: 'hidden' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -120,
                        left: -120,
                        width: 440,
                        height: 440,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: '50%',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -80,
                        right: -80,
                        width: 320,
                        height: 320,
                        bgcolor: 'rgba(255,255,255,0.04)',
                        borderRadius: '50%',
                        pointerEvents: 'none',
                    }}
                />
                <Container maxWidth="xl" sx={{ maxWidth: 900, position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Eyebrow white>Careers at TrustLab</Eyebrow>
                    </Box>
                    <Typography className="serif" sx={{ color: '#fff', fontSize: { xs: 30, md: 42 }, fontWeight: 600, mt: 2, mb: 2 }}>
                        Join us in fixing diagnostics for India.
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', lineHeight: 1.75, mb: 4, maxWidth: 560, mx: 'auto', fontWeight: 300 }}>
                        We're hiring pathologists, engineers, Trustians, designers and operators. If you believe diagnostics should
                        work for people — not against them — we want to hear from you.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate(`${ROUTES.CAREERS}#openings`)}
                            sx={{
                                bgcolor: '#fff',
                                color: 'var(--ink)',
                                '&:hover': { bgcolor: '#fff', transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.15)' },
                            }}
                        >
                            View Open Roles →
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => navigate(`${ROUTES.CAREERS}#why-tdpl`)}
                            sx={{
                                color: '#fff',
                                borderColor: 'rgba(255,255,255,0.3)',
                                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' },
                            }}
                        >
                            Life at TrustLab
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', mt: 4 }}>
                        {[
                            ['100+', 'Team members'],
                            ['13', 'Cities hiring'],
                            ['4.5★', 'Users rating'],
                        ].map(([v, l]) => (
                            <Box key={l} sx={{ textAlign: 'center' }}>
                                <Typography className="serif" sx={{ fontSize: 30, color: 'rgba(255,255,255,0.95)', lineHeight: 1 }}>
                                    {v}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>{l}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ───────────── 11. CONTACT ───────────── */}
            <SectionWrap bg="var(--cream)">
                <Box id="contact" />
                <SectionHeader
                    eyebrow="Get In Touch"
                    title="We'd love to hear from you."
                    description="Patient support — reach us through any of these channels."
                    align="center"
                />
                <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 3, md: 4 } }}>
                    {contactChannels.map((c) => (
                        <Grid key={c.t} item xs={12} md={4}>
                            <Box
                                sx={{
                                    bgcolor: '#fff',
                                    border: '1px solid var(--border-l)',
                                    borderRadius: '20px',
                                    p: 4,
                                    textAlign: 'center',
                                    height: '100%',
                                    transition: 'all 0.25s',
                                    '&:hover': {
                                        borderColor: 'var(--sage-p)',
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 32px rgba(61,122,82,0.08)',
                                    },
                                }}
                            >
                                <Typography sx={{ fontSize: 32, mb: 2 }}>{c.ico}</Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', mb: 0.75 }}>{c.t}</Typography>
                                <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 300, mb: 2 }}>{c.sub}</Typography>
                                <Typography
                                    className="mono"
                                    sx={{ fontSize: 15, fontWeight: 700, color: 'var(--sage)', wordBreak: 'break-word' }}
                                >
                                    {c.val}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </SectionWrap>
        </Box>
    );
};

export default About;
